import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";

import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";

import { Observable, Subscription } from "rxjs";
import { skip } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import { changePassword } from "src/app/store/actions/users/user.actions";
import { AppState, usersStateIsLoading, userStatePasswordChanged } from "src/app/store/reducers";

import { DialogData } from "src/app/models/dialog/dialog.model";
import { FormUtils } from "src/app/shared/utils/formUtils";

const CHANGE_PASS_FORM = {
  currentPassword: "currentPassword",
  newPassword: "newPassword",
  confirmPassword: "confirmPassword",
};

@Component({
  selector: "app-password-dialog",
  templateUrl: "./password-dialog.component.html",
  styleUrls: ["./password-dialog.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class PasswordDialogComponent implements OnInit, OnDestroy {
  message = "TableTemplate.PasswordDialog.Message";
  confirmButtonText = "TableTemplate.PasswordDialog.AcceptButton";
  cancelButtonText = "TableTemplate.PasswordDialog.CancelButton";

  changePassWordForm: FormGroup;

  isLoading$: Observable<boolean>;

  // Password Changed Observable and Subscription
  passwordChanged$: Observable<boolean>;
  passwordChangedSub: Subscription;

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: DialogData,
    private dialogRef: MatDialogRef<PasswordDialogComponent>,
    private fb: FormBuilder,
    private store: Store<AppState>
  ) {
    this.isLoading$ = this.store.pipe(select(usersStateIsLoading));
    this.passwordChanged$ = this.store.pipe(select(userStatePasswordChanged));
  }

  ngOnInit(): void {
    this.createForm();
    this.setPasswordChangedSubscription();
  }

  createForm() {
    this.changePassWordForm = this.fb.group({
      [CHANGE_PASS_FORM.currentPassword]: new FormControl("", [
        Validators.required,
        FormUtils.noWhitespaceValidator,
        Validators.minLength(6),
        Validators.maxLength(55),
      ]),
      [CHANGE_PASS_FORM.newPassword]: new FormControl("", [
        Validators.required,
        FormUtils.noWhitespaceValidator,
        Validators.minLength(6),
        Validators.maxLength(55),
      ]),
      [CHANGE_PASS_FORM.confirmPassword]: new FormControl("", [
        Validators.required,
        FormUtils.noWhitespaceValidator,
        Validators.minLength(6),
        Validators.maxLength(55),
      ]),
    });
  }

  setPasswordChangedSubscription() {
    this.passwordChangedSub = this.passwordChanged$.pipe(skip(1)).subscribe((passwordChanged) => {
      if (passwordChanged) {
        this.changePassWordForm.reset();
        this.dialogRef.close(true);
      }
    });
  }

  changePassword() {
    if (!this.changePassWordForm.valid) {
      this.changePassWordForm.markAllAsTouched();
      this.changePassWordForm.markAsDirty();
      return;
    }

    this.store.dispatch(
      changePassword({
        passwords: {
          currentPass: this.changePassWordForm.controls.currentPassword.value,
          newPass: this.changePassWordForm.controls.newPassword.value,
          username: localStorage.getItem("username"),
        },
      })
    );
  }

  /** Custom Validator to check if the new Password is the same of the confirmation */
  passwordMismatchValidator() {
    if (
      !this.changePassWordForm.controls[CHANGE_PASS_FORM.confirmPassword]
        .pristine
    ) {
      const mismatch =
        this.changePassWordForm.controls[CHANGE_PASS_FORM.newPassword].value ===
        this.changePassWordForm.controls[CHANGE_PASS_FORM.confirmPassword].value
          ? false
          : true;

      if (mismatch && this.changePassWordForm !== undefined) {
        this.changePassWordForm.controls[
          CHANGE_PASS_FORM.newPassword
        ].setErrors({
          passwordMismatch: true,
        });
      }
      if (!mismatch && this.changePassWordForm !== undefined) {
        this.changePassWordForm.controls[
          CHANGE_PASS_FORM.newPassword
        ].setErrors({
          passwordMismatch: null,
        });
        this.changePassWordForm.controls[
          CHANGE_PASS_FORM.newPassword
        ].updateValueAndValidity();
      }
      this.passwordSameValidator();
    }
  }

  /** Custom Validator to check is new Password is the same of the current */
  passwordSameValidator() {
    const mismatch =
      this.changePassWordForm.controls[CHANGE_PASS_FORM.newPassword].value ===
      this.changePassWordForm.controls[CHANGE_PASS_FORM.currentPassword].value
        ? true
        : false;

    if (mismatch && this.changePassWordForm !== undefined) {
      if (
        (this.changePassWordForm.controls[CHANGE_PASS_FORM.newPassword]
          .touched ||
          this.changePassWordForm.controls[CHANGE_PASS_FORM.newPassword]
            .dirty) &&
        !this.changePassWordForm.controls[CHANGE_PASS_FORM.currentPassword]
          ?.errors
      ) {
        this.changePassWordForm.controls[
          CHANGE_PASS_FORM.currentPassword
        ].setErrors({
          samePassword: true,
          required: false,
        });
      }
    }
    if (!mismatch && this.changePassWordForm !== undefined) {
      if (
        this.changePassWordForm.controls[CHANGE_PASS_FORM.currentPassword]
          .value === ""
      ) {
        this.changePassWordForm.controls[
          CHANGE_PASS_FORM.currentPassword
        ].setErrors({
          samePassword: false,
          required: true,
        });
      } else {
        this.changePassWordForm.controls[
          CHANGE_PASS_FORM.currentPassword
        ].setErrors({
          samePassword: null,
          required: null,
        });
        this.changePassWordForm.controls[
          CHANGE_PASS_FORM.currentPassword
        ].updateValueAndValidity();
      }
    }
  }

  ngOnDestroy() {
    if (this.passwordChangedSub) {
      this.passwordChangedSub.unsubscribe();
    }
  }
}
